/**
 * Zustand der KI-Textaktionen an EINEM Feld — bewusst als reine Funktionen,
 * ohne React, damit die Rückgängig-Logik testbar ist.
 *
 * Zwei Dinge werden gemerkt:
 *  - `history`: die Werte VOR jeder KI-Aktion (Stapel, letzter zuerst).
 *    Damit lässt sich jede Aktion einzeln zurücknehmen, auch mehrere
 *    hintereinander („Kürzen, dann Fachsprache, dann doch zurück").
 *  - `applied`: der zuletzt von der KI gesetzte Wert. Nur solange er
 *    unverändert im Feld steht, gilt der Text als ungeprüft und wird farblich
 *    gekennzeichnet — sobald der Nutzer selbst tippt, ist es sein Text.
 */

export interface AiTextState {
  readonly history: readonly string[];
  readonly applied: string | null;
}

export const emptyAiTextState: AiTextState = { history: [], applied: null };

/**
 * Obergrenze des Rückgängig-Stapels. Ohne Deckel wächst er bei jedem Klick
 * weiter; mehr als ein paar Schritte zurück will hier niemand.
 */
export const MAX_UNDO_STEPS = 10;

/**
 * Registriert eine angewandte KI-Aktion: `previous` wandert auf den Stapel,
 * `next` gilt als aktuell gesetzter KI-Wert.
 */
export function applyAiText(state: AiTextState, previous: string, next: string): AiTextState {
  const history = [previous, ...state.history].slice(0, MAX_UNDO_STEPS);
  return { history, applied: next };
}

export interface UndoResult {
  state: AiTextState;
  /** Wert, der wieder ins Feld geschrieben werden muss. */
  value: string;
}

/**
 * Nimmt die letzte KI-Aktion zurück. Liefert `null`, wenn nichts auf dem
 * Stapel liegt — der Aufrufer darf den Knopf dann gar nicht erst anbieten.
 */
export function undoAiText(state: AiTextState): UndoResult | null {
  const [previous, ...rest] = state.history;
  if (previous === undefined) return null;
  // `applied` zeigt nach dem Zurücknehmen auf den wiederhergestellten Wert:
  // stammt der selbst aus einer KI-Aktion, bleibt die Kennzeichnung zu Recht
  // stehen; war es der ursprüngliche Nutzertext, verschwindet sie über
  // `isUnreviewed` (Stapel ist dann leer bzw. der Wert ist der Ausgangswert).
  const applied = rest.length > 0 ? previous : null;
  return { state: { history: rest, applied }, value: previous };
}

export function canUndoAiText(state: AiTextState): boolean {
  return state.history.length > 0;
}

/**
 * Steht im Feld gerade unverändert KI-Text? Das ist die Bedingung für die
 * farbliche Kennzeichnung „noch nicht überarbeitet".
 */
export function isUnreviewedAiText(state: AiTextState, currentValue: string): boolean {
  return state.applied !== null && state.applied !== '' && currentValue === state.applied;
}
